import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DataTable from "react-data-table-component";
import { GetData, DeleteData } from "../../services/api";
import Loader from "../../components/Common/Loader";
import Swal from "sweetalert2";
import moment from "moment";
import PublishModal from "./PublishModal";

const Pages = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [publishPage, setPublishPage] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  // Una página está lista cuando tiene content.html sin placeholders __CREADS_
  const isStillGenerating = (page) =>
    !page.content || (page.content?.html || "").includes("__CREADS_");

  // Auto-refresh silencioso cada 5s mientras haya páginas generando imágenes
  useEffect(() => {
    const hasGenerating = pages.some(isStillGenerating);
    if (!hasGenerating) return;
    const timer = setTimeout(() => fetchPages(true), 5000);
    return () => clearTimeout(timer);
  }, [pages]);

  /**
   * @param {boolean} silent - true = refresca sin mostrar el spinner global
   *   (usado por el auto-refresh para no ocultar la tabla mientras las badges actualizan)
   */
  const fetchPages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await GetData("/pages");
      setPages(response.data || []);
    } catch (error) {
      console.error("Error fetching pages:", error);
      if (!silent) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar las páginas",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E9553E",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await DeleteData(`/pages/${id}`);
        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: "La página ha sido eliminada",
        });
        fetchPages();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar la página",
        });
      }
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      venta_directa: "Venta Directa",
      webinar: "Webinar",
      gracias: "Página de Gracias",
      linktree: "Link in Bio",
      vsl: "VSL",
      captura: "Captura",
    };
    return types[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      venta_directa: "warning",
      webinar: "info",
      gracias: "success",
      linktree: "secondary",
      vsl: "primary",
      captura: "danger",
    };
    return colors[type] || "secondary";
  };

  const filteredPages = pages.filter(
    (page) =>
      page.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      getTypeLabel(page.type)?.toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    {
      name: "Nombre",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <h6 className="mb-0">{row.name}</h6>
          {row.publishedUrl ? (
            <a href={row.publishedUrl} target="_blank" rel="noopener noreferrer" className="text-primary" style={{ fontSize: 11, wordBreak: "break-all" }}>
              {row.publishedUrl}
            </a>
          ) : row.slug ? (
            <small className="text-muted">/{row.slug}</small>
          ) : null}
        </div>
      ),
    },
    {
      name: "Tipo",
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => (
        <Badge color={getTypeBadgeColor(row.type)}>
          {getTypeLabel(row.type)}
        </Badge>
      ),
    },
    {
      name: "Estado",
      selector: (row) => row.isPublished,
      sortable: true,
      cell: (row) => (
        <div>
          {!row.content ? (
            <Badge color="warning">
              <span className="spinner-border spinner-border-sm me-1" style={{width:'0.6rem',height:'0.6rem'}} />
              Generando...
            </Badge>
          ) : (row.content?.html || "").includes("__CREADS_") ? (
            <Badge color="warning">
              <span className="spinner-border spinner-border-sm me-1" style={{width:'0.6rem',height:'0.6rem'}} />
              Generando imágenes...
            </Badge>
          ) : (
            <Badge color={row.isPublished ? "success" : "secondary"}>
              {row.isPublished ? "Publicada" : "Borrador"}
            </Badge>
          )}
          {!row.isActive && (
            <Badge color="danger" className="ms-1">Inactiva</Badge>
          )}
        </div>
      ),
    },
    {
      name: "Creada",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) =>
        row.createdAt
          ? moment(row.createdAt).format("DD/MM/YYYY")
          : "-",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="button" className="btn btn-sm btn-link">
            <i className="mdi mdi-dots-vertical"></i>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              onClick={() => navigate(`/pages/${row.id}/visual-editor`)}
              disabled={isStillGenerating(row)}
              title={isStillGenerating(row) ? "Espera a que termine la generación de imágenes" : ""}
            >
              <i className={`mdi ${isStillGenerating(row) ? "mdi-timer-sand" : "mdi-pencil"} me-2`}></i>
              {isStillGenerating(row) ? "Generando..." : "Editar visual"}
            </DropdownItem>
            <DropdownItem onClick={() => navigate(`/pages/${row.id}/edit`)}>
              <i className="mdi mdi-robot me-2"></i>Editar brief (IA)
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem
              onClick={() => setPublishPage(row)}
              disabled={isStillGenerating(row)}
              className={row.isPublished ? "text-success" : ""}
            >
              <i className={`mdi ${row.isPublished ? "mdi-earth" : "mdi-earth"} me-2`}></i>
              {row.isPublished ? "Administrar publicación" : "Publicar"}
            </DropdownItem>
            {row.isPublished && row.publishedUrl && (
              <DropdownItem onClick={() => window.open(row.publishedUrl, "_blank")}>
                <i className="mdi mdi-open-in-new me-2"></i>Ver página
              </DropdownItem>
            )}
            <DropdownItem divider />
            <DropdownItem
              onClick={() => handleDelete(row.id)}
              className="text-danger"
            >
              <i className="mdi mdi-delete me-2"></i>Eliminar
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title="IHG" breadcrumbItem="Páginas" />
        <Row className="mb-3">
          <Col className="col-sm-auto">
            <Button
              color="primary"
              className="add-btn"
              onClick={() => navigate("/pages/new")}
            >
              <i className="ri-add-line align-bottom me-1"></i> Nueva Página
            </Button>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end">
              <div className="search-box ms-2">
                <input
                  type="text"
                  className="form-control search"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            {loading ? (
              <Loader size={150} />
            ) : (
              <Card>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredPages}
                    noDataComponent={
                      <span className="py-4">No hay páginas disponibles</span>
                    }
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50]}
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <PublishModal
        isOpen={!!publishPage}
        toggle={() => setPublishPage(null)}
        page={publishPage}
        onPublished={() => fetchPages()}
      />
    </div>
  );
};

export default Pages;

